import { useI18n } from '../shared/i18n/I18nProvider';
import { useReducer, useState, type ReactNode } from 'react';
import { DEMO_DATE, demoStores } from './data';
import { createDemoState, demoReducer } from './demoState';
import { prepareDemoBooking, type BookingAdjustment } from './bookingDecision';
import { validateDemoInvoice } from './invoiceMutation';
import { cashChange } from '../modules/invoices/model/checkout';
import { startOfStoreDay } from '../shared/lib/format';
import { validateSettings } from '../modules/settings/model/settings';
import { validateEmployee } from '../modules/staff/model/staffDirectory';
import { DemoContext } from './DemoContext';

export function DemoProvider({ children }: { children: ReactNode }) {
  const { t } = useI18n();

  const [selectedStore, setStore] = useState(demoStores[0]);
  const [state, dispatch] = useReducer(demoReducer, undefined, createDemoState);
  const store = { ...selectedStore, name: state.settings.find(s => s.store_id === selectedStore.id)?.name || selectedStore.name };
  function approveBooking(id: string, adjustment: BookingAdjustment) {
    const item = state.bookings.find(b => b.id === id && b.store_id === store.id);
    if (!item) throw new Error(t("Không tìm thấy yêu cầu trong cửa hàng hiện tại."));
    const appointment = prepareDemoBooking(item, adjustment, state.appointments, store.id);
    dispatch({ type: 'booking/approve', id, storeId: store.id, appointment, station: adjustment.station });
  }
  return <DemoContext.Provider value={{ ...state, store, setStore, approveBooking,
    addAppointment: item => dispatch({ type: 'appointment/add', item }),
    addCustomers: items => dispatch({ type: 'customers/add', items: items.map(item => ({ ...item, store_id: store.id })) }),
    saveService: item => dispatch({ type: 'service/save', item: { ...item, store_id: store.id } }),
    saveCustomer: item => dispatch({ type: 'customer/save', item: { ...item, store_id: store.id } }),
    addExpense: item => dispatch({ type: 'expense/add', item: { ...item, store_id: store.id } }),
    saveEmployee: item => { const scoped = { ...item, store_id: store.id }; validateEmployee(scoped); dispatch({ type: 'employee/save', item: scoped }); },
    saveSettings: item => { const scoped = { ...item, store_id: store.id }; validateSettings(scoped); dispatch({ type: 'settings/save', item: scoped }); },
    addInvoice: item => { const scoped = { ...item, store_id: store.id }; validateDemoInvoice(state, scoped, store.id); dispatch({ type: 'invoice/add', item: scoped }); },
    settleInvoice: (id, method, cash) => {
      const item = state.invoices.find(i => i.id === id && i.store_id === store.id && i.status === 0);
      if (!item) throw new Error(t("Hóa đơn không còn chờ thanh toán trong cửa hàng này."));
      const paidAt = Math.max(item.bill_date_ms, startOfStoreDay(DEMO_DATE, store.time_zone_id || 'Asia/Ho_Chi_Minh') + 12 * 3600000);
      const next = { ...item, status: 1, paid_amount: item.total, payment_method: method, cash_tendered: method === 'cash' ? cash : undefined, change_amount: method === 'cash' ? cashChange(item.total, cash ?? 0) : undefined, paid_at: paidAt };
      validateDemoInvoice(state, next, store.id); dispatch({ type: 'invoice/pay', item: next, storeId: store.id });
    },
    declineBooking: (id, reason) => dispatch({ type: 'booking/decline', id, storeId: store.id, reason }),
  }}>{children}</DemoContext.Provider>;
}
